import type { ReactNode } from 'react'

import React from 'react'
import * as ReactDOMServer from 'react-dom/server'

import { PANE_LAYER } from './constants'

type CustomOverlayViewArgs = {
  position: google.maps.LatLngLiteral
  content?: ReactNode
  paneLayer?: PANE_LAYER
  onClick?: () => void
}

const createHTMLMapMarker = (
  args: CustomOverlayViewArgs,
  OverlayView = google.maps.OverlayView,
) => {
  class CustomOverlayView extends OverlayView {
    position: google.maps.LatLngLiteral

    paneLayer: PANE_LAYER

    containerDiv: HTMLElement

    onClick: (() => void) | undefined

    constructor({
      position,
      content,
      paneLayer = PANE_LAYER.floatPane,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick,
    }: CustomOverlayViewArgs) {
      super()
      this.position = position
      this.paneLayer = paneLayer

      if (onClick) {
        this.onClick = onClick
      }

      this.containerDiv = document.createElement('div')
      this.containerDiv.style.cssText =
        'cursor: auto; height: 0; position: absolute; width: 200px; z-index: 999;'

      if (content) {
        let markup = ''
        if (React.isValidElement(content)) {
          markup = ReactDOMServer.renderToStaticMarkup(content as any)
        } else if (typeof content === 'string' || typeof content === 'number') {
          markup = String(content)
        }
        this.containerDiv.innerHTML = markup
      }
    }

    /** Called when the Overlay is added to the map. */
    onAdd() {
      this.getPanes()?.[this.paneLayer].appendChild(this.containerDiv)
      CustomOverlayView.preventMapHitsFrom(this.containerDiv)

      if (this.onClick) {
        this.containerDiv.addEventListener('click', this.onClick)
      }
    }

    /** Called when the Overlay is removed from the map. */
    onRemove() {
      if (this.containerDiv.parentElement) {
        this.containerDiv.parentElement.removeChild(this.containerDiv)
      }
    }

    /** Called each frame when the Overlay needs to draw itself. */
    draw() {
      const divPosition = this.getProjection().fromLatLngToDivPixel(this.position) || { x: 0, y: 0 }
      // Hide the Overlay when it is far out of view.
      const display =
        Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none'

      if (display === 'block') {
        this.containerDiv.style.left = `${divPosition.x}px`
        this.containerDiv.style.top = `${divPosition.y}px`
      }

      if (this.containerDiv.style.display !== display) {
        this.containerDiv.style.display = display
      }
    }
  }

  return new CustomOverlayView(args)
}

export default createHTMLMapMarker
